import { FiltersType, ListingRequest, ListingResponse } from '@/api/types';
import { Account, Department, Manager } from '@/client/accounts';
import { Branch } from '@/client/branches';
import {
  CampaignPhishingSignEnum,
  CampaignUserResultStatusesEnum,
} from '@/client/campaigns';
import { Benchmark } from '@/client/campaigns/types/benchmarks.type';
import {
  Course,
  CourseAttempt,
  CourseStatusAttemptEnum,
  MaterialType,
} from '@/client/courses';
import { Group } from '@/client/groups';
import { User } from '@/client/users';

export type CourseCompletionReportsType = {
  availableCourses: number;
  enrolledCourses: number;
  totalEnrolments: number;
  totalCompletions: number;
};

export type userAwarenessReportsType = {
  usersWithCompletedCourses: number;
  usersWithEnrolledCourses: number;
  usersWithoutCompletedCourses: number;
  usersWithoutCourses: number;
  usersNeverLoggedIn: number;
  usersWithOverDueCoursesAndMaterials: number;
};

export type UsersSummaryReport = {
  account: Account;
  dataSubmitted: number;
  emailsOpened: number;
  emailsSent: number;
  enrolledCourses: number;
  lastAction: CampaignUserResultStatusesEnum;
  linksClicked: number;
  notCompletedCourses: number;
  overdueCourses: number | null;
  phishingCampaigns: number;
  emailsReported: number;
  user: User;
  averageScore: number | null;
  lastLogin: Date | null;
};

export type UserReportsMeta = {
  companyName: string;
  department: string;
  manager: string;
  country: string;
  jobTitle: string;
  mobilePhone: string;
  officeLocation: string;
};

export type UserAwarenessBranch = {
  id: string;
  name: string;
  parent: { id: string; name: string };
};

export type UserAwarenessCourse = {
  id: string;
  name: string;
  score: number | null;
  status: CourseStatusAttemptEnum;
  completed: Date;
  enrollmentDate: Date;
  dueDate: Date | null;
  type: MaterialType;
};

export type UserAwareness = {
  id: string;
  enrollmentDate: Date;
  dueDate: Date | null;
  user: User;
  course: Pick<Course, 'id' | 'type' | 'name'>;
  bestAttempt: Pick<CourseAttempt, 'id' | 'score' | 'completed' | 'status'>;
  unEnrolledAt: Date | null;
  unEnrolledBy?: User;
  status: CourseStatusAttemptEnum;
};

export type KeyStatsReport = {
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
  usersCount: number;
};

export type PerformanceOvertimeReport = {
  period: string;
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
};

export type PhishingSignReports = {
  phishingSign: CampaignPhishingSignEnum;
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
};

export type DepartmentPerformanceReport = {
  name: string;
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
  usersCount: number;
};

export type SimulationCampaignsReport = {
  campaignId: string;
  name: string;
  phishingSign: CampaignPhishingSignEnum;
  benchmark: Benchmark;
  launchDate: Date;
  finishDate: Date;
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
  clickedRateBenchmark: number;
  submittedRateBenchmark: number;
  hasFalsePositives?: boolean;
};

export type VulnerableUser = {
  id: string;
  email: string;
  username: string;
  department: string;
  name: string;
};

export type VulnerableUsersReport = {
  user: VulnerableUser;
  sentCount: number;
  clickedCount: number;
  submittedCount: number;
  reportedCount: number;
  clickedRate: number;
  submittedRate: number;
  reportedRate: number;
};

export type VulnerableUsersOverTimeReport = {
  period: string;
  percentageRiskyUsers: number;
};

export type ReportRequest = {
  from?: Date;
  to?: Date;
  managers?: string[];
  departments?: string[];
  groups?: string[];
  branches?: string[];
};

export type DataRange = {
  label: string;
  value: {
    from?: Date;
    to?: Date;
  };
};

export enum DashboardFilterType {
  ALL_DATA = 'all_data',
  MANAGERS = 'managers',
  DEPARTMENTS = 'departments',
  BRANCHES = 'branches',
  GROUPS = 'groups',
}

export enum GroupedByEntityFilterType {
  MANAGER = 'manager',
  DEPARTMENT = 'department',
  BRANCH = 'branch',
  GROUP = 'group',
}
export interface DashboardFilterValue {
  type?: DashboardFilterType;
  managers?: Manager[];
  departments?: Department[];
  branches?: Branch[];
  groups?: Group[];
}

export type ListPerformanceOvertimeReportResponse = {
  result: PerformanceOvertimeReport[];
};

export type ListKeyStatsReportResponse = {
  result: KeyStatsReport[];
};

export type ListPhishingSignReportsResponse = {
  result: PhishingSignReports[];
};

export type ListDepartmentPerformanceReportResponse = {
  result: DepartmentPerformanceReport[];
};

export type ListSimulationCampaignsReportResponse = {
  result: SimulationCampaignsReport[];
};

export type ListVulnerableUsersReportResponse =
  ListingResponse<VulnerableUsersReport>;

export type ListVulnerableUsersOverTimeReportResponse = {
  result: VulnerableUsersOverTimeReport[];
};

export type AttemptsReport = {
  status: string;
  language: string;
  count: number;
};

export type ListUsersSummaryReportRequest = ListingRequest;
export type ListUsersSummaryReportResponse =
  ListingResponse<UsersSummaryReport> & {
    lastGeneratedAt?: Date;
  };

export type ListUsersAwarenessReportRequest = ListingRequest;
export type ListUsersAwarenessReportResponse = ListingResponse<UserAwareness>;

export type ExportUsersReportRequest = {
  filters: FiltersType | undefined;
  exportKey: string;
  sort: string[];
  columns?: string[];
};

export type ExportUsersFormValues = {
  isAllData: boolean;
  areAllColumns: boolean;
};

export type ExportReportFormValues = {
  isAllData: boolean;
  areAllColumns: boolean;
};

export enum UsersReportType {
  AWARENESS = 'awareness',
  SUMMARY = 'summary',
  PHISHING = 'phishing',
}

export enum ExportType {
  AWARENESS = 'awareness',
  SUMMARY = 'summary',
  PHISHING = 'phishing',
  EXECUTIVE_SUMMARY = 'executive_summary',
  ACCOUNT_USERS = 'account_users',
  BRANCH_USERS = 'branch_users',
  GROUP_USERS = 'group_users',
  ACCOUNTS = 'accounts',
}
